import { Component, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MenuDrawerComponent } from '../shared/menu-drawer/menu-drawer.component';
import { NotificationDrawerComponent } from '../shared/notification-drawer/notification-drawer.component';
import { ToolbarComponent } from '../shared/toolbar/toolbar.component';
import { Route, Router, RouterOutlet, Routes } from '@angular/router';
import { NavigationItem } from '../models/navigationItem.model';
import { Settings } from '../models/settings';
import { PermissionService } from '../services/permission/permission.service';
import { LoginService } from '../services/login/login.service';

@Component({
  selector: 'app-pages',
  standalone: true,
  imports: [
    MatSidenavModule,
    MatButtonModule,
    ToolbarComponent,
    MenuDrawerComponent,
    NotificationDrawerComponent,
    RouterOutlet,
  ],
  templateUrl: './pages.component.html',
  styleUrl: './pages.component.scss',
})
export class PagesComponent implements OnInit {
  settings = new Settings();

  // Add here routes that is not supposed to be included in the nav building.
  ignoredRoutes!: string[];
  baseIgnoredRoutes: string[]=[
    'login',
    '**',
    'privacy-policy',
    'change-password',
    'contact-us',
  ]
  routeData!: NavigationItem[];
  systemRouteData!: NavigationItem[];
  private hasSubscribed: boolean = false;

  constructor(private router: Router, private permissionService: PermissionService, private loginService: LoginService) {}

  ngOnInit(): void {
    this.ignoredRoutes = this.baseIgnoredRoutes;
    this.updateIgnoredRoutes();
    this.buildNavigation();

    // re-render when navigation
    if (!this.hasSubscribed) {
      this.loginService.warehouseChanged$.subscribe(() => {
        this.rebuildNavigation();
        console.log("Navigation is rebuilt.")
      })
      this.hasSubscribed = true;
    }
  }

  private updateIgnoredRoutes(): void {
    this.settings.setSettingsMap(localStorage.getItem('settings') || '{}');
    this.ignoredRoutes = [...this.baseIgnoredRoutes];

    const isLoaOnly = this.settings.getLoaOrContract();
    const showContract = this.settings.getShowContract();

    if (!isLoaOnly) {
      // LOA/Contract is false — show only contract, so ignore LOA
      this.ignoredRoutes.push('loa');
    } else if (isLoaOnly && !showContract) {
      // LOA is true, contract not allowed — show only LOA, so ignore contract
      this.ignoredRoutes.push('contract');
    }
    // else (isLoaOnly && showContract) => show both; do not exclude anything

    console.log('Ignored Routes:', this.ignoredRoutes);
  }
  
  private rebuildNavigation(): void {
    this.routeData = [];
    this.systemRouteData = [];
    this.updateIgnoredRoutes();
    this.buildNavigation();
  }

  async buildNavigation() {
    const allRoutes = this.router.config;
    const navigationData = await this.extractRoutes(allRoutes);
    const normalizedData = this.normalizeRoutes(navigationData);

    // manually clean emptyPaths at the end.
    // some emptyPaths contains actual routes.
    // we get the actual routes and then remove them.

    // get main routes
    this.routeData = normalizedData.filter(
      (r) => r.path !== '' && !r.metaData.isSystemPage
    );

    // get system routes
    this.systemRouteData = normalizedData.filter(
      (r) => r.path !== '' && r.metaData.isSystemPage
    );

    // permission checking main routes
    this.routeData = this.routeData.filter((r: any) => {
      if (r.children?.length) {
        r.children = r.children.filter((childRoute: any) => 
          this.permissionService.hasPermission(childRoute.permission)
        );
        return r.children.length > 0;
      }
      return this.permissionService.hasPermission(r.permission);
    });

    // permission checking system routes
    // note: system routes only supports 1 level
    this.systemRouteData = this.systemRouteData
      .map((r: any) => { 
        if (this.permissionService.hasPermission(r.permission)) {
          return r;
        }
      });
    
  }

  async extractRoutes(routes: Route[]): Promise<NavigationItem[]> {
    const routePromises = routes.map(async (route: Route) => {
      if (route.loadChildren) {
        if (typeof route.loadChildren === 'function') {
          const childRoutes = await (
            route.loadChildren as () => Promise<{ default: Routes }>
          )();

          // recursive
          const childRouteData = await this.extractRoutes(childRoutes.default);

          if (!this.ignoredRoutes.includes(route.path!)) {
            return {
              permission: route?.data?.['permission'] || null,
              metaData: route?.data?.['meta'],
              children: childRouteData,
              path: route.path!,
            };
          }
        }
      } else {
        if (!this.ignoredRoutes.includes(route.path!)) {
          return {
            permission: route?.data?.['permission'] || null,
            metaData: route?.data?.['meta'],
            children: [],
            path: route.path!,
          };
        }
      }
      return null;
    });

    const resolvedPaths = await Promise.all(routePromises);

    return resolvedPaths.filter((r) => r !== null);
  }

  normalizeRoutes(routes: Route[]) {
    const result: any[] = [];
    routes.forEach((route) => {
      if (route.path === '') {
        route?.children?.forEach((child) => {
          result.push(this.cleanEmptyPaths(child));
        });
      } else {
        result.push(this.cleanEmptyPaths(route));
      }
    });

    return result;
  }

  cleanEmptyPaths(route: Route) {
    if (route.children && route.children.length > 0) {
      route.children = route.children
        .filter((child) => child.path !== '')
        .map((child) => this.cleanEmptyPaths(child)); //recursive
    }
    return route;
  }
}
